/**
 * Username and email normalization.
 *
 * These functions are the single authoritative definition of "normalized"
 * used to derive `username_normalized` / `email_normalized` before they are
 * compared against the database's unique indexes (docs/APP_PLAN.md §7.1,
 * APP_SPEC.md §9–§10, TECH_STACK.md §31–§32). The API must apply the same
 * function used here before every uniqueness-sensitive read or write
 * (registration, login lookup, search, availability checks) so normalization
 * can never drift between call sites.
 *
 * These functions only normalize; they do not validate length or charset.
 * Charset/length validation is the responsibility of `packages/validation`
 * and the database CHECK constraints, which must both stay in agreement
 * with `USERNAME_CONSTRAINTS` in `@perplexicon/config`.
 */

/**
 * Normalize a username for case-insensitive uniqueness, lookup, and search.
 *
 * Applies Unicode NFKC normalization (so visually/semantically equivalent
 * code point sequences collapse to one form) followed by lowercasing.
 *
 * The original user-entered string must be stored separately as
 * `username_display` and is never altered by this function.
 */
export function normalizeUsername(usernameDisplay: string): string {
  return usernameDisplay.normalize('NFKC').toLowerCase();
}

/**
 * Normalize an email address for case-insensitive uniqueness and lookup.
 *
 * Trims leading/trailing whitespace and lowercases the result. Deliberately
 * does not apply any provider-specific munging (e.g. Gmail dot-insensitivity
 * or `+tag` stripping) — see docs/APP_PLAN.md §7.1 ("trim + lowercase (no
 * provider-specific munging)").
 */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}
